/**
 * `.m-modal-side` — the Add Outcomes modal's filter sidebar (CAE-1553).
 *
 * The same `AdminInput` / `AdminSelect` pair the Add Fixtures modal and the
 * Catalogue's own sidebar use, so an operator meets their filters in the same
 * place spelled the same way, and `shared/modal-add-outcomes.jsx`'s own `aside`
 * in the same order: search, competition, sport, market type, status, then the
 * options group with its one toggle.
 */
import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';
import { AdminInput, AdminSelect, SearchIcon, Toggle } from '../../../../components/ui';
import {
  STATUS_OPTIONS,
  type AddOutcomesFacets,
  type AddOutcomesFilterState,
} from './add-outcomes-filters';

const SectionTitle = ({ children }: { children: string }) => (
  <Box
    component="h3"
    sx={(theme: Theme) => ({
      margin: 0,
      fontSize: '11px',
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: theme.tokens.color.ink4,
    })}
  >
    {children}
  </Box>
);

export interface AddOutcomesSidebarProps {
  filters: AddOutcomesFilterState;
  onChange: (next: Partial<AddOutcomesFilterState>) => void;
  facets: AddOutcomesFacets;
}

export const AddOutcomesSidebar = ({ filters, onChange, facets }: AddOutcomesSidebarProps) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
    {/*
     * No "Filters" heading. Both handoff screenshots open the aside straight
     * onto SEARCH — `08-modals/screenshots/02-add-outcomes.png` here and
     * `01-add-fixtures.png` on the sibling — and the sidebar is already the
     * only thing in that column, so a label naming it says nothing the controls
     * underneath do not. "Options" stays: it separates the toggle from the
     * facets above it, which is a break the operator cannot otherwise see.
     */}
    <AdminInput
      label="Search"
      type="search"
      icon={<SearchIcon />}
      placeholder="Fixtures, markets…"
      value={filters.search}
      onChange={(value) => onChange({ search: value })}
    />
    <AdminSelect
      label="Competition"
      allLabel="All competitions"
      options={facets.competitions.map((name) => ({ value: name, label: name }))}
      value={filters.competition}
      onChange={(value) => onChange({ competition: value })}
    />
    <AdminSelect
      label="Sport"
      allLabel="All sports"
      options={facets.sports}
      value={filters.sport}
      onChange={(value) => onChange({ sport: value })}
    />
    <AdminSelect
      label="Market type"
      allLabel="All market types"
      options={facets.marketTypes.map((name) => ({ value: name, label: name }))}
      value={filters.marketType}
      onChange={(value) => onChange({ marketType: value })}
    />
    <AdminSelect
      label="Status"
      allLabel="Any status"
      options={STATUS_OPTIONS}
      value={filters.status}
      onChange={(value) => onChange({ status: value })}
    />

    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '6px' }}>
      <SectionTitle>Options</SectionTitle>
      <Toggle
        checked={filters.hideOnReel}
        onChange={(checked) => onChange({ hideOnReel: checked })}
        label="Hide outcomes already on this reel"
      />
    </Box>
  </Box>
);
